import axios, { AxiosError, AxiosResponse } from "axios";
import {
  Button,
  Container,
  Form,
  Input,
  Link,
  Message,
  Stack,
  Text,
  useBasisFormContext,
  validateInput,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { EnhancedAccount } from "library/accounts/types";
import { ApiError, ErrorType } from "library/api/address/types.generated";
import { components } from "library/api/card-management-service.generated";
import { postHref } from "library/api/httpMethods";
import { useNav } from "pages/ManagePersonalDetails/hooks/useNav";
import { Dispatch } from "react";
import { UseFormReturn } from "react-hook-form";
import { useMutation } from "react-query";
import { AccountHeader } from "../components/AccountHeader";
import { FullPersonalDetails } from "../types";
import { getCountryCodePrefix, getCreateSessionUrl } from "../utils";
import { Actions, ActionTypes, PhoneNumbersFormValues } from "./types";
import {
  getFullMobileNumber,
  getVerifyLinks,
  validateMobileNumberMap,
} from "./utils";

type Props = {
  account: EnhancedAccount;
  fullPersonalDetails: FullPersonalDetails;
  dispatch: Dispatch<Actions>;
  token2FaRenewed?: "yes";
  methods: UseFormReturn<PhoneNumbersFormValues>;
};

const isTest = process.env.NODE_ENV === "test";

export const MobileNumberForm = ({
  dispatch,
  account,
  fullPersonalDetails,
  token2FaRenewed,
  methods,
}: Props) => {
  const { Field } = useBasisFormContext<PhoneNumbersFormValues>();
  const { goBack } = useNav();

  const residentialCountry = fullPersonalDetails.residential.country;
  const countryCodePrefix = getCountryCodePrefix(residentialCountry);
  const validateMobileNumber =
    (residentialCountry && validateMobileNumberMap[residentialCountry]) ??
    validateInput;

  const token = account.credit_card?.token;

  const updateMutation = useMutation<
    AxiosResponse<components["schemas"]["MobilePhoneUpdateResponse"]>,
    AxiosError<ApiError>,
    { values: PhoneNumbersFormValues; token2FaRenewed?: string }
  >(({ values }) =>
    postHref(
      fullPersonalDetails.updatePhoneEndpoint,
      { mobile_number: values.mobileNumber },
      isTest
        ? {
            "fake-session-expiry": token2FaRenewed!, // header values are always strings
          }
        : {},
    ),
  );

  const handleError = (
    mobileNumber: string,
    error: AxiosError<ApiError> | unknown,
  ) => {
    if (!axios.isAxiosError(error)) {
      return dispatch({ type: ActionTypes.ERROR });
    }

    const errorType = error.response?.data?.type ?? null;
    const createSessionUrl = getCreateSessionUrl({
      links: error.response?.data.links,
    });

    if (
      errorType === ErrorType.REQUIRED_2FA &&
      createSessionUrl !== "" &&
      !!token
    ) {
      return dispatch({
        type: ActionTypes.EXPIRED_OTP,
        payload: { createSessionUrl, token, mobileNumber },
      });
    }

    return dispatch({ type: ActionTypes.ERROR });
  };

  const onSubmit = async (values: PhoneNumbersFormValues) => {
    if (!token) {
      dispatch({ type: ActionTypes.ERROR });
      return;
    }

    const fullMobileNumber = getFullMobileNumber(
      countryCodePrefix,
      values.mobileNumber,
    );

    try {
      const response = await updateMutation.mutateAsync({
        values: { mobileNumber: fullMobileNumber },
      });
      const { updatePhoneVerify, updatePhoneResend } = getVerifyLinks(
        response.data.links,
      );
      dispatch({
        type: ActionTypes.VERIFY,
        payload: {
          mobileNumber: fullMobileNumber,
          token,
          updatePhoneVerify,
          updatePhoneResend,
        },
      });
      /**
       * 🚨 TS 4.4 Catch Error defaulting to the unknown Type.
       * https://devblogs.microsoft.com/typescript/announcing-typescript-4-4/#use-unknown-catch-variables
       */
    } catch (err: unknown) {
      handleError(values.mobileNumber, err);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Stack gap="10">
        <AccountHeader account={account} />
        <Stack gap="4">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Mobile number
          </Text>
          <Text>{fullPersonalDetails.mobilePhone}</Text>
        </Stack>
        <Container width="100%" width-sm="400">
          <Field
            as={Input}
            label="New mobile number"
            name="mobileNumber"
            type="tel"
            prefix={countryCodePrefix}
            validate={validateMobileNumber}
          />
        </Container>
        <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
          <strong>Verify your number</strong>
          <br />
          You will be sent a verification code to confirm your new mobile
          number. To update your details or if your mobile is an international
          number, please visit Latitude{" "}
          <Link href="/contact" newTab={false}>
            contact support
          </Link>
          .
        </Message>
        <Container width="100%" width-sm="400">
          <FormActions>
            <FormActions.Primary>
              <Button type="submit" loading={updateMutation.isLoading}>
                Next
              </Button>
            </FormActions.Primary>
            <FormActions.Secondary>
              <Button
                variant="secondary"
                disabled={updateMutation.isLoading}
                onClick={goBack}
              >
                Back
              </Button>
            </FormActions.Secondary>
          </FormActions>
        </Container>
      </Stack>
    </Form>
  );
};
